// /admin/v1/apps — mirrors backend handlers/app/admin.go.

import { del, get, list, patch, post, put } from './client';
import type { ListResult } from './client';

export type AppStatus = 'draft' | 'active' | 'archived';

export interface App {
  id: number;
  slug: string;
  name: string;
  icon_url?: string | null;
  status: AppStatus;
  theme?: Record<string, unknown> | null;
  routes?: Record<string, unknown> | null;
  config?: Record<string, unknown> | null;
  /** Per-App frontend copy bundle (P1 §3 lego-ization). Edit in /apps/:id Copy tab. */
  copy_json?: Record<string, unknown> | null;
  /** Per-App flow definition (flow-schema). Shape: {steps:[...]}. 空=默认答题流. */
  flow_config?: { steps?: string[] } | null;
  owner_admin_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ListAppsQuery {
  q?: string;
  status?: AppStatus;
  page?: number;
  page_size?: number;
}

export function listApps(q: ListAppsQuery = {}): Promise<ListResult<App>> {
  return list<App>('/admin/v1/apps', q);
}

export function getApp(id: number): Promise<App> {
  return get<App>(`/admin/v1/apps/${id}`);
}

export interface CreateAppInput {
  slug: string;
  name: string;
  icon_url?: string;
  theme?: Record<string, unknown>;
  routes?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export function createApp(input: CreateAppInput): Promise<App> {
  return post<App>('/admin/v1/apps', input);
}

// ============== 起 App 向导 (P0.4) ==============
// One-shot cross-table provision: apps + workflow binding + prompts +
// content_package + 2 SKUs + prices. Backend runs everything in one tx.
export interface WizardInput {
  slug: string;
  name: string;
  primary_color?: string;
  workflow_code: string; // 必须是已存在的 workflow_templates.code
  output_count?: number; // 套图张数 (1 = 单张人物像, 4 = 套系). 默认 1.
  system_prompt?: string;
  story_prompt?: string;
  unlock_price_cents?: number;
  physical_card_price_cents?: number;
  package_code?: string;
  prompt_template_code?: string;
  unlock_sku_code?: string;
  physical_card_sku_code?: string;
  owner_admin_id?: string;
}

export interface WizardResult {
  app: App;
  workflow_code: string;
  unlock_sku_id: number;
  physical_card_sku_id: number;
}

export function runAppWizard(input: WizardInput): Promise<WizardResult> {
  return post<WizardResult>('/admin/v1/apps/wizard', input);
}

// ============== Per-App prompt overlay (app_prompts, 000013) ==============
export interface AppPromptRow {
  id: number;
  app_id: number;
  workflow_code: string;
  prompt_key: string;
  body: string;
}

export function listAppPrompts(id: number): Promise<{ prompts: AppPromptRow[] | null }> {
  return get<{ prompts: AppPromptRow[] | null }>(`/admin/v1/apps/${id}/prompts`);
}

// prompts: key→body. 空 body 的 key 会被后端当 delete (回退 workflow 默认).
export function upsertAppPrompts(
  id: number,
  workflowCode: string,
  prompts: Record<string, string>,
): Promise<{ prompts: AppPromptRow[] | null }> {
  return put<{ prompts: AppPromptRow[] | null }>(
    `/admin/v1/apps/${id}/prompts/${workflowCode}`,
    { prompts },
  );
}

export interface UpdateAppInput {
  name?: string;
  icon_url?: string;
  theme?: Record<string, unknown>;
  routes?: Record<string, unknown>;
  config?: Record<string, unknown>;
  copy_json?: Record<string, unknown>;
  flow_config?: { steps?: string[] };
}

export function updateApp(id: number, input: UpdateAppInput): Promise<App> {
  return patch<App>(`/admin/v1/apps/${id}`, input);
}

export function setAppStatus(id: number, status: AppStatus): Promise<App> {
  return post<App>(`/admin/v1/apps/${id}/status`, { status });
}

export function archiveApp(id: number): Promise<App> {
  return post<App>(`/admin/v1/apps/${id}/archive`);
}

export function publishApp(id: number): Promise<App> {
  return post<App>(`/admin/v1/apps/${id}/publish`);
}

// Platform bindings live under /apps/:id/platforms/:platform. Listed for
// completeness — used by the App detail page's "platforms" tab.

export type PlatformKind = 'wx_miniapp' | 'dy_miniapp' | 'h5' | 'ios';

export interface PlatformBinding {
  app_id: number;
  platform: PlatformKind;
  external_appid?: string;
  publish_status?: 'draft' | 'published' | 'in_review';
  updated_at?: string;
}

export function listPlatformBindings(id: number): Promise<ListResult<PlatformBinding>> {
  return list<PlatformBinding>(`/admin/v1/apps/${id}/platforms`);
}

export function upsertPlatformBinding(
  id: number,
  platform: PlatformKind,
  body: { external_appid: string; secret?: string },
): Promise<PlatformBinding> {
  return put<PlatformBinding>(`/admin/v1/apps/${id}/platforms/${platform}`, body);
}

export function deletePlatformBinding(id: number, platform: PlatformKind): Promise<void> {
  return del(`/admin/v1/apps/${id}/platforms/${platform}`);
}
